import React from 'react';
import type { CatalogEntry } from '../../services/inferenceApi';
import { formatPrice } from '../../utils/catalog';
import ProviderBadge from './ProviderBadge';

interface CompareSheetProps { a: CatalogEntry; b: CatalogEntry; currency: string; }

type Row = {
  label: string;
  render: (m: CatalogEntry) => React.ReactNode;
  better?: (a: CatalogEntry, b: CatalogEntry) => 'a' | 'b' | null; // null = tie/not comparable
};

const cmp = (av: number, bv: number, lowerWins: boolean): 'a' | 'b' | null => {
  if (av === bv) return null;
  return (lowerWins ? av < bv : av > bv) ? 'a' : 'b';
};

const resolvedCurrency = (m: CatalogEntry, envelopeCurrency: string) => m.currency || envelopeCurrency;

export default function CompareSheet({ a, b, currency }: CompareSheetProps) {
  // Winner-highlighting requires both sides priced, same billing_unit, and same resolved currency;
  // mismatched units or missing pricing block highlighting. Entries sharing the same unrecognized
  // unit are still compared numerically. For tiered entries, input_price_per_1m is already the
  // default tier's resolved price (guaranteed by the catalog API), so the price-row comparison
  // below compares default tiers only - it never accounts for cheaper/pricier non-default slabs.
  const bothPriced = a.pricing_available && b.pricing_available && a.billing_unit === b.billing_unit
    && resolvedCurrency(a, currency) === resolvedCurrency(b, currency);
  const formattedA = formatPrice(a, currency);
  const formattedB = formatPrice(b, currency);
  const outputA = formattedA?.output ?? null;
  const outputB = formattedB?.output ?? null;
  const bothOutputPriced = bothPriced && outputA !== null && outputB !== null;
  // A side is unit-billed when it has a price but no separate output leg
  // (formatPrice returns output: null for per-image/second/etc. billing).
  // The shared price-row label only makes sense as "Input price" when both
  // sides are token-billed - otherwise (either side unit-billed) it's just
  // "Price", since there's no input/output split to speak of.
  const unitBilled = (formattedA !== null && formattedA.output === null)
    || (formattedB !== null && formattedB.output === null);
  const priceLabel = unitBilled ? 'Price' : 'Input price';
  const rows: Row[] = [
    { label: 'Category', render: (m) => <span className="capitalize">{m.category || '-'}</span> },
    {
      label: 'Context length',
      render: (m) => <span className="font-mono">{m.context_length.toLocaleString()}</span>,
      better: (x, y) => cmp(x.context_length, y.context_length, false),
    },
    {
      label: 'Parameters',
      render: (m) => <span className="font-mono">{m.parameters_b > 0 ? `${m.parameters_b}B` : '-'}</span>,
    },
    {
      label: priceLabel,
      render: (m) => (
        <span className="font-mono">
          {formatPrice(m, currency)?.input ?? 'Pricing unavailable'}
          {m.price_tiers.length > 0 && (
            <span className="ml-1 text-xs text-ps-subtle">({m.price_tiers.length} slabs)</span>
          )}
        </span>
      ),
      better: bothPriced ? (x, y) => cmp(x.input_price_per_1m, y.input_price_per_1m, true) : undefined,
    },
    {
      label: 'Output price',
      render: (m) => {
        const p = formatPrice(m, currency);
        return <span className="font-mono">{p ? (p.output ?? '-') : 'Pricing unavailable'}</span>;
      },
      better: bothOutputPriced ? (x, y) => cmp(x.output_price_per_1m, y.output_price_per_1m, true) : undefined,
    },
    { label: 'Tags', render: (m) => m.tags.length ? m.tags.join(', ') : '-' },
    { label: 'About', render: (m) => <span className="text-ps-muted">{m.description || '-'}</span> },
  ];
  const cell = (side: 'a' | 'b', row: Row) => {
    const winner = row.better?.(a, b) ?? null;
    const highlighted = winner === side;
    return (
      <td className={`!whitespace-normal align-top ${highlighted ? 'text-ps-accent bg-ps-accent-soft/60 font-medium' : 'text-ps-text'}`}>
        {row.render(side === 'a' ? a : b)}
      </td>
    );
  };
  return (
    <div className="ps-card overflow-x-auto">
      <table className="ps-tbl min-w-[560px] table-fixed">
        <colgroup>
          <col className="w-[26%]" />
          <col className="w-[37%]" />
          <col className="w-[37%]" />
        </colgroup>
        <thead>
          <tr>
            <th className="align-bottom">Metric</th>
            {[a, b].map((m) => (
              <th key={m.id} className="!normal-case !tracking-normal !text-[13px] !py-3.5 align-bottom">
                <div className="flex items-center gap-2.5 min-w-0">
                  {m.provider && <ProviderBadge name={m.provider} logoUrl={m.provider_logo_url} size={28} />}
                  <div className="min-w-0">
                    {m.provider && <div className="text-xs font-normal text-ps-subtle truncate">{m.provider}</div>}
                    <div className="font-semibold text-ps-text truncate">{m.display_name}</div>
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td className="!whitespace-normal align-top text-ps-muted">{row.label}</td>
              {cell('a', row)}
              {cell('b', row)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
